import React from 'react';
import { ChevronLeft, ChevronRight, Play, Pause, ArrowRight, ShieldCheck, Truck, Lock } from 'lucide-react';

const ANIMATION_CLASS = {
  'fade-up': 'animate-dpc-fade-up',
  fade: 'animate-dpc-fade-up',
  slide: 'animate-dpc-fade-up',
};

const TRUST_STRIP = [
  { icon: ShieldCheck, label: 'Premium quality' },
  { icon: Truck, label: 'Fast delivery' },
  { icon: Lock, label: 'Secure payment' },
];

/**
 * Full-bleed editorial hero driven entirely by admin-managed HeroSlide records.
 * Honors slide.textColor, buttonStyle, animationType and accent — previously
 * saved by the admin dashboard but never applied on the storefront.
 */
const Hero = ({
  loading,
  error,
  displayedSlides,
  currentSlide,
  setCurrentSlide,
  isPlaying,
  setIsPlaying,
  mousePosition,
  handleMouseMove,
  setMousePosition,
  sectionsRef,
  onSlideAction,
}) => {
  return (
    <section
      ref={(el) => { sectionsRef.current.home = el; }}
      className="relative h-[100vh] min-h-[560px] sm:min-h-[640px] md:h-screen overflow-hidden bg-ink"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setMousePosition({ x: 0, y: 0 })}
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center text-paper/70 font-body text-sm dpc-label">
          Loading the collection…
        </div>
      )}
      {!loading && error && (
        <div className="absolute inset-0 flex items-center justify-center text-red-200 text-sm">
          {error}
        </div>
      )}
      {!loading && !error && displayedSlides.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center text-paper/60 text-sm">
          No slides available
        </div>
      )}

      {!loading && !error && displayedSlides.map((slide, index) => {
        const isActive = index === currentSlide;
        const textColor = slide.textColor || '#F1EDE6';
        const accent = slide.accent || '#A2401E';
        const animationClass = ANIMATION_CLASS[slide.animationType] || ANIMATION_CLASS['fade-up'];
        const isOutlineButton = slide.buttonStyle === 'outline';

        return (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-[1200ms] ease-out ${isActive ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-[8000ms] ease-out"
              style={{
                backgroundImage: `url(${slide.image})`,
                transform: isActive
                  ? `scale(1.06) translate(${mousePosition.x * 0.15}px, ${mousePosition.y * 0.08}px)`
                  : 'scale(1)',
              }}
            />
            {slide.gradientEnabled && (
              <div className={`absolute inset-0 bg-gradient-to-t ${slide.gradient || 'from-black/70 via-black/10 to-transparent'}`} />
            )}
            {!slide.gradientEnabled && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
            )}
            {/* Guarantees text legibility regardless of what the admin's source image looks like in this zone */}
            <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-black/60 to-transparent" />

            <div className="relative h-full flex items-end z-10">
              <div className="w-full px-6 sm:px-10 md:px-14 pb-20 sm:pb-24 md:pb-28 max-w-3xl">
                {slide.badge && (
                  <span
                    className={`inline-block dpc-label px-4 py-1.5 rounded-full mb-6 border ${isActive ? animationClass : ''}`}
                    style={{ color: accent, borderColor: accent, background: 'rgba(241,237,230,0.92)' }}
                  >
                    {slide.badge}
                  </span>
                )}

                <h2
                  className={`font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] mb-5 ${isActive ? animationClass : ''}`}
                  style={{ color: textColor, animationDelay: '0.08s' }}
                >
                  {slide.title}
                </h2>

                {slide.subtitle && (
                  <p
                    className={`text-lg sm:text-xl mb-3 font-medium ${isActive ? animationClass : ''}`}
                    style={{ color: textColor, opacity: 0.92, animationDelay: '0.18s' }}
                  >
                    {slide.subtitle}
                  </p>
                )}

                {slide.description && (
                  <p
                    className={`text-sm sm:text-base mb-8 max-w-xl leading-relaxed ${isActive ? animationClass : ''}`}
                    style={{ color: textColor, opacity: 0.78, animationDelay: '0.26s' }}
                  >
                    {slide.description}
                  </p>
                )}

                <div className={`flex flex-wrap gap-4 ${isActive ? animationClass : ''}`} style={{ animationDelay: '0.34s' }}>
                  {slide.buttonText && (
                    <button
                      onClick={() => onSlideAction(slide.buttonAction, 'products')}
                      className="group flex items-center gap-2.5 px-6 sm:px-7 py-3.5 dpc-label rounded-full transition-transform duration-200 hover:scale-[1.03]"
                      style={
                        isOutlineButton
                          ? { color: textColor, border: `1.5px solid ${textColor}`, background: 'transparent' }
                          : { background: accent, color: '#F1EDE6' }
                      }
                    >
                      {slide.buttonText}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  )}
                  {slide.secondaryButtonText && (
                    <button
                      onClick={() => onSlideAction(slide.secondaryButtonAction, 'about')}
                      className="dpc-label px-6 sm:px-7 py-3.5 rounded-full border transition-colors duration-200"
                      style={{ color: textColor, borderColor: `${textColor}66` }}
                    >
                      {slide.secondaryButtonText}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Slide controls */}
      {displayedSlides.length > 1 && (
        <>
          <button
            onClick={() => setCurrentSlide((currentSlide - 1 + displayedSlides.length) % displayedSlides.length)}
            className="hidden sm:flex absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 items-center justify-center w-10 h-10 rounded-full bg-paper/15 hover:bg-paper/30 backdrop-blur-sm text-paper transition-colors"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => setCurrentSlide((currentSlide + 1) % displayedSlides.length)}
            className="hidden sm:flex absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 items-center justify-center w-10 h-10 rounded-full bg-paper/15 hover:bg-paper/30 backdrop-blur-sm text-paper transition-colors"
            aria-label="Next slide"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="absolute right-6 sm:right-10 md:right-14 bottom-8 z-20 flex items-center gap-3">
            <button
              onClick={() => setIsPlaying((p) => !p)}
              className="text-paper/70 hover:text-paper transition-colors"
              aria-label={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
            >
              {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            </button>
            <div className="flex items-center gap-2">
              {displayedSlides.map((slide, index) => (
                <button
                  key={slide.id}
                  onClick={() => setCurrentSlide(index)}
                  className="relative w-1.5 h-1.5 rounded-full transition-all duration-300"
                  style={{
                    background: index === currentSlide ? '#F1EDE6' : 'rgba(241,237,230,0.35)',
                    width: index === currentSlide ? '20px' : '6px',
                  }}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Top progress line */}
      {displayedSlides.length > 1 && (
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-paper/10 z-20">
          <div
            className="h-full bg-accent transition-all duration-500"
            style={{ width: `${((currentSlide + 1) / displayedSlides.length) * 100}%` }}
          />
        </div>
      )}
    </section>
  );
};

export const TrustBar = () => (
  <div className="bg-ink py-5">
    <div className="max-w-6xl mx-auto px-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
      {TRUST_STRIP.map(({ icon: Icon, label }) => (
        <div key={label} className="flex items-center gap-2.5 text-paper/85">
          <Icon className="w-4 h-4 text-brass" />
          <span className="dpc-label">{label}</span>
        </div>
      ))}
    </div>
  </div>
);

export default Hero;
